/** @jsx h */
/** @jsxFrag Fragment */
import { CSSProperties, h } from "preact";
import { clamp } from "@utils/utils";
import { HsvaColor, hsvaToHslaString, hsvaToHslString } from "@utils/color";
import { Interaction, Interactive, Pointer } from "./Interactive.tsx";

interface AlphaProps {
  className?: string;
  hsva: HsvaColor;
  style: CSSProperties;
  onChange: (newAlpha: { a: number }) => void;
}

export const Alpha = (props: AlphaProps) => {
  const { className, hsva, onChange } = props;
  const handleMove = (interaction: Interaction) => {
    onChange({ a: interaction.left });
  };

  const handleKey = (offset: Interaction) => {
    // Alpha always fit into [0, 1] range
    onChange({ a: clamp(hsva.a + offset.left, 0, 1) });
  };

  // We use `Object.assign` instead of the spread operator
  // to prevent adding the polyfill (about 150 bytes gzipped)
  const colorFrom = hsvaToHslaString(Object.assign({}, hsva, { a: 0 }));
  const colorTo = hsvaToHslaString(Object.assign({}, hsva, { a: 1 }));

  const gradientStyle = {
    backgroundImage: `linear-gradient(90deg, ${colorFrom}, ${colorTo})`,
  };

  const ariaValue = Math.round(hsva.a * 100);

  return (
    <Interactive
      style={{ ...props.style, ...gradientStyle }}
      onMove={handleMove}
      onKey={handleKey}
      aria-label="Alpha"
      aria-valuetext={`${ariaValue}%`}
      aria-valuenow={ariaValue}
      aria-valuemin="0"
      aria-valuemax="100"
    >
      <Pointer
        className="react-colorful__alpha-pointer"
        left={hsva.a}
        color={hsvaToHslaString(hsva)}
      />
    </Interactive>
  );
};

interface Props {
  className?: string;
  hue: number;
  onChange: (newHue: { h: number }) => void;
  style: CSSProperties;
}

export const HueBase = ({ className, hue, onChange, style }: Props) => {
  const handleMove = (interaction: Interaction) => {
    onChange({ h: 360 * interaction.left });
  };

  const handleKey = (offset: Interaction) => {
    // Hue measured in degrees of the color circle ranging from 0 to 360
    onChange({
      h: clamp(hue + offset.left * 360, 0, 360),
    });
  };

  return (
    <Interactive
      style={style}
      onMove={handleMove}
      onKey={handleKey}
      aria-label="Hue"
      aria-valuenow={Math.round(hue)}
      aria-valuemax="360"
      aria-valuemin="0"
    >
      <Pointer
        className="react-colorful__hue-pointer"
        left={hue / 360}
        color={hsvaToHslString({ h: hue, s: 100, v: 100, a: 1 })}
      />
    </Interactive>
  );
};

interface SaturationProps {
  hsva: HsvaColor;
  style: CSSProperties;

  onChange: (newColor: { s: number; v: number }) => void;
}

export const SaturationBase = (props: SaturationProps) => {
  const { hsva, onChange } = props;
  const handleMove = (interaction: Interaction) => {
    onChange({
      s: interaction.left * 100,
      v: 100 - interaction.top * 100,
    });
  };

  const handleKey = (offset: Interaction) => {
    // Saturation and brightness always fit into [0, 100] range
    onChange({
      s: clamp(hsva.s + offset.left * 100, 0, 100),
      v: clamp(hsva.v - offset.top * 100, 0, 100),
    });
  };

  return (
    <Interactive
      style={{
        ...props.style,
        backgroundColor: hsvaToHslString({ h: hsva.h, s: 100, v: 100, a: 1 }),
      }}
      onMove={handleMove}
      onKey={handleKey}
      aria-label="Color"
      aria-valuetext={`Saturation ${Math.round(hsva.s)}%, Brightness ${Math.round(hsva.v)}%`}
    >
      <Pointer
        className="react-colorful__saturation-pointer"
        top={1 - hsva.v / 100}
        left={hsva.s / 100}
        color={hsvaToHslString(hsva)}
      />
      <div
        style={{
          zIndex: 0,
          position: "absolute",
          width: 100,
          height: 100,
          background:
            `linear-gradient(0deg,#000,transparent),linear-gradient(90deg,#fff,hsla(0,0%,100%,0))`,
        }}
      >
      </div>
    </Interactive>
  );
};

export interface ColorPickerProps {
  hsva: HsvaColor;
  onChange: (v: Partial<HsvaColor>) => void;
}
export function ColorPicker(props: ColorPickerProps) {
  return (
    <div class="picker" style={{ position: "absolute" }}>
      <SaturationBase
        style={{
          position: "relative",
          height: 100,
          width: 100,
        }}
        onChange={props.onChange}
        hsva={props.hsva}
      />

      <HueBase
        hue={props.hsva.h}
        onChange={props.onChange}
        style={{
          position: "relative",
          height: 25,
          width: 100,
          background:
            `linear-gradient(90deg,red 0,#ff0 17%,#0f0 33%,#0ff 50%,#00f 67%,#f0f 83%,red)`,
        }}
      />
      <Alpha
        style={{
          position: "relative",
          height: 25,
          width: 100,
        }}
        hsva={props.hsva}
        onChange={props.onChange}
      />
    </div>
  );
}
